import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { PatreonTierString } from '@furality/boop-client/models';
import { ReportError, useMutation, useQuery } from '@furality/frontend-stack';
import boop from '#/api/boop.ts';
import useToasts from '@/lib/useToast.ts';

const SUCCESS_PARAM = 'patreonLinked';
const CONNECTION_TYPE = 'patreon';
const RETURN_PATH_KEY = 'patreonRedirectPath';

const successQueryParam = new URLSearchParams({ [SUCCESS_PARAM]: 'true' }).toString();

export const usePatreonRedirect = () => {
  useEffect(() => {
    if (!window.location.search.includes(successQueryParam)) {
      return;
    }

    const returnPath = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);

    if (returnPath != null && returnPath !== window.location.pathname) {
      window.location.replace(`${returnPath}?${successQueryParam}`);
    }
  }, []);
};

type Patron = {
  name: string;
  active: boolean;
  tier?: PatreonTierString;
};

const usePatreon = () => {
  const { pushToast } = useToasts();
  const { t } = useTranslation();

  const patreon = useQuery({
    queryKey: ['linkedConnections'],
    queryFn: async () => {
      const response = await boop.value.connectionAPIGetLinkedConnections();
      switch (response.status) {
        case 200:
          return response.data;
        default:
          throw new ReportError(response);
      }
    },
    select: (response) => response.connections.find((c) => c.type === CONNECTION_TYPE),
  });
  const patreonBoopMeta = useQuery({
    queryKey: ['patreonBoopMeta'],
    queryFn: async () => {
      const response = await boop.value.userAPIGetUserPatreon({
        params: {
          id: '@me',
        },
      });
      switch (response.status) {
        case 200:
          return response.data;
        default:
          throw new ReportError(response);
      }
    },
  });

  const link = async () => {
    sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname);
    const redirectUri = `${window.location.origin}/?${successQueryParam}`;
    const response = await boop.value.connectionAPILinkConnection({
      params: {
        connection: CONNECTION_TYPE,
        redirect_uri: redirectUri,
      },
    });
    switch (response.status) {
      case 200:
        window.location.href = response.data.redirectUrl;
        return;
      default:
        throw new ReportError(response);
    }
  };

  const unlink = async () => {
    const response = await boop.value.connectionAPIUnlinkUserConnection({
      params: {
        connection: CONNECTION_TYPE,
      },
    });

    switch (response.response.status) {
      case 200:
        return 'ok';
      default:
        throw new ReportError(response);
    }
  };

  const linkPatreon = useMutation({
    mutationFn: async () => {
      try {
        await link();
      } catch (e) {
        pushToast('error', t('shared.patreon.link_error'));
        throw e;
      }
    },
  });

  const unlinkPatreon = useMutation({
    mutationFn: async () => {
      try {
        await unlink();
      } catch (e) {
        pushToast('error', t('shared.patreon.unlink_error'));
        throw e;
      }
    },
  });
  const unlinkAndRelinkPatreon = useMutation({
    mutationFn: async () => {
      if (patreon.data === undefined) {
        pushToast('error', t('shared.patreon.link_error'));
      }
      try {
        await unlink();
        await link();
      } catch (e) {
        pushToast('error', t('shared.patreon.link_error'));
        throw e;
      }
    },
  });

  return {
    patreon:
      patreon.isSuccess && patreonBoopMeta.isSuccess && patreon.data
        ? ({
            tier: patreonBoopMeta.data.tier,
            name: patreon.data.remoteDisplayName,
            active: patreonBoopMeta.data.isPatron,
          } satisfies Patron)
        : undefined,
    isPatreonLoading:
      patreon.isLoading ||
      linkPatreon.isPending ||
      patreonBoopMeta.isLoading ||
      unlinkPatreon.isPending ||
      unlinkAndRelinkPatreon.isPending,
    connectToPatreon: () => linkPatreon.mutate(),
    updatePatreon: () => unlinkAndRelinkPatreon.mutate(),
    unlinkPatreon: () => unlinkPatreon.mutate(),
  };
};

export default usePatreon;
